/**
 * Pages/Panel/ReportesNomina/ReporteIncidencias.js
 *
 * Faltas, permisos, incapacidades, vacaciones, retardos y horas extra por empleado,
 * con tres cortes del mismo dato:
 *
 *   General     -> un renglón por empleado, los totales de todo el rango.
 *   Por nómina  -> un renglón por empleado y prenómina, para ver en qué periodo pasó.
 *   Por días    -> rejilla empleado × día, el calendario del mes. Es la que deja ver
 *                  patrones (quién falta los lunes, los puentes, las ausencias en racimo).
 *
 * ── LAS COLUMNAS ─────────────────────────────────────────────────────────────
 * Primero un RESUMEN de seis cubetas fijas y luego una columna por cada TipoIncidencia
 * que de verdad apareció en el rango. Las cubetas son el espejo de `sumaIncidencia` del
 * motor de nómina (la regla vive en el backend, en Utils/incidenciasReporte.utils.js), así
 * que este reporte no puede contradecir al recibo.
 *
 * Ojo con "Faltas": agrupa FINJ + PSS + CAST, igual que la variable FALTAS del cálculo.
 * El desglose fino es justo para lo que están las columnas de detalle.
 *
 * ── DÍAS Y HORAS NO SE MEZCLAN ───────────────────────────────────────────────
 * `TipoIncidencia.tipo` decide la unidad (2=Días, 3=Horas). Retardos y horas extra son
 * HORAS y van en sus propias columnas, marcadas "(hrs)". Sumarlas junto a las faltas daría
 * un número que no significa nada, así que ni la fila de totales las junta.
 *
 * ── POR QUÉ `Table` DE ANTD Y NO `TableItem` ─────────────────────────────────
 * `TableItem` fuerza `tableLayout: fixed` e IGNORA el `scroll` que se le pase. Esta matriz
 * necesita `scroll.x` calculado (crece con el número de columnas) y `fixed: "left"` en las
 * columnas de identidad. Mismo motivo y mismo precedente que ReportePercepcionesPorPeriodo.
 */

import { message, PageHeader, Select, Space, Spin, Table, Tabs, Tooltip } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Form,
  FormItem,
  RangePickerItem,
  SelectItem,
  SwitchItem,
  SegmentedItem,
  ButtonItem,
  ErrorItem,
} from "../../../Components/Items";
import SelectDepartamentosItem from "../../../Components/SelectDepartamentosItem";
import SelectEmpleadosItem from "../../../Components/SelectEmpleadosItem";
import SelectPuestosItem from "../../../Components/SelectPuestosItem";
import { FileExcelOutlined, InfoCircleOutlined } from "@ant-design/icons";
import * as XLSXStyle from "xlsx-js-style";
import moment from "moment";
import "moment/locale/es";
// Reporte PROPIO de Biwo (no reproduce un layout de cliente), así que va con la paleta de
// la marca: el headerStyle cian de ExcelEstilos, no el gris de estilosExcelNomina.
import { headerStyle, border } from "../../../Components/ExcelEstilos";
import { celda, celdaNumero, filaDe, textoStyle, centroStyle } from "./estilosExcelNomina";

moment.locale("es");

const { Option } = Select;
const { TabPane } = Tabs;

// ── Paleta ───────────────────────────────────────────────────────────────────
const TEAL_OSCURO = "007A8A";
const AZUL_SUAVE = "D6F4F8";
const AZUL_TENUE = "F0FEFF";

// Un color por cubeta, el mismo en la pantalla y en la rejilla, para que el ojo agarre el
// tipo de ausencia sin leer la clave.
const COLOR_CUBETA = {
  faltas: "#A8071A",
  permisoGoce: "#1677FF",
  incapacidades: "#D48806",
  vacaciones: "#389E0D",
  retardos: "#C41D7F",
  horasExtra: "#5A4FCC",
};
const COLOR_SIN_CUBETA = "#8C8C8C";

const colorDeCubeta = (cubeta) => COLOR_CUBETA[cubeta] || COLOR_SIN_CUBETA;

const VISTAS = [
  { value: "general", label: "General" },
  { value: "nomina", label: "Por nómina" },
  { value: "dias", label: "Por días" },
];

const AYUDA_CUBETA = {
  faltas:
    "Ausencias sin goce de sueldo. Agrupa faltas injustificadas, permisos sin goce y días "
    + "de castigo (FINJ + PSS + CAST), igual que la variable FALTAS del cálculo de nómina. "
    + "El desglose está en las columnas de detalle.",
  permisoGoce: "Ausencias que sí se pagan (seConsidera = Ausencia con derecho a sueldo).",
  incapacidades: "Todo tipo marcado como Incapacidad: riesgo de trabajo, enfermedad general, maternidad.",
  vacaciones: "Días de vacaciones pagados en el periodo.",
  retardos: "Horas de retardo acumuladas. Se miden en HORAS, no en días.",
  horasExtra: "Horas extra (simples, dobles, triples y las que el catálogo tenga). En HORAS.",
};

const tituloStyle = {
  font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: TEAL_OSCURO } },
  alignment: { horizontal: "left", vertical: "center" },
};
const subtituloStyle = { font: { sz: 11, bold: true } };
const notaStyle = { font: { sz: 9, italic: true, color: { rgb: "595959" } } };

// Las columnas del resumen llevan franja tenue para despegarse del detalle por tipo.
const resumenNumeroStyle = {
  font: { bold: true },
  fill: { fgColor: { rgb: AZUL_TENUE } },
  alignment: { horizontal: "center", vertical: "center" },
  numFmt: "#,##0.##",
  border,
};
const totalTextoStyle = {
  font: { bold: true },
  fill: { fgColor: { rgb: AZUL_SUAVE } },
  alignment: { horizontal: "center", vertical: "center" },
  border,
};
const totalNumeroStyle = { ...totalTextoStyle, numFmt: "#,##0.##" };

// ── Colores del Excel ────────────────────────────────────────────────────────
//
// Mismo código de color que la pantalla, pero INVERTIDO: en la hoja el color va de
// RELLENO y el texto en el tono oscuro. Los colores saturados de la pantalla
// (#A8071A y compañía) como fondo dejan el texto ilegible, y una celda de Excel no
// tiene el tooltip que en pantalla explica qué es.
//
// **Sólo se pinta la celda que TIENE algo.** Un cero se queda con su estilo normal:
// el contraste contra las celdas sin dato es justo lo que deja ver de un vistazo
// quién tuvo qué, y pintar la hoja entera lo anularía. Aplica igual a las tres
// vistas — en la rejilla al día con incidencia, y en General / Por nómina a la cifra
// distinta de cero.
const RELLENO_CUBETA = {
  faltas: { fill: "FFD6D6", font: "A8071A" },
  permisoGoce: { fill: "D6E4FF", font: "0958D9" },
  incapacidades: { fill: "FFF1D6", font: "AD6800" },
  vacaciones: { fill: "D9F7BE", font: "237804" },
  retardos: { fill: "FFD6E7", font: "C41D7F" },
  horasExtra: { fill: "EFDBFF", font: "391085" },
};
// Una incidencia cuyo tipo ya no está en el catálogo no cae en ninguna cubeta: va en
// gris para que se distinga de las clasificadas sin desaparecer.
const RELLENO_SIN_CUBETA = { fill: "F0F0F0", font: "595959" };

const estiloCubeta = (cubeta) => {
  const c = RELLENO_CUBETA[cubeta] || RELLENO_SIN_CUBETA;
  return {
    font: { bold: true, sz: 9, color: { rgb: c.font } },
    fill: { fgColor: { rgb: c.fill } },
    alignment: { horizontal: "center", vertical: "center" },
    border,
  };
};

// El mismo color pero para una CIFRA. Sin el numFmt la celda sale con formato General y
// un 1.5 de retardo se vería distinto al del resto de la columna.
const estiloCubetaNumero = (cubeta) => ({ ...estiloCubeta(cubeta), numFmt: "#,##0.##" });

// 0 se pinta gris claro: una rejilla llena de ceros negros esconde los números que importan.
const CERO = <span style={{ color: "#d9d9d9" }}>0</span>;

// Cantidades de incidencia: enteras casi siempre, pero medio día y media hora existen.
// Sin trailing zeros para que la rejilla no se llene de ".00".
const num = (v) => {
  const n = Number(v) || 0;
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
};

class ReporteIncidencias extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    consultado: false,
    vista: "general",
    empresa: null,
    rango: null,
    resumen: [],
    columnas: [],
    dias: [],
    periodos: [],
    filas: [],
    trabajadores: 0,
    descartadosPorRelacion: 0,
    sinClasificar: [],
    objetoRegistrosPatronales: null,
    objetoTipos: null,
  };

  atras() {
    this.props.history.goBack();
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  componentDidMount() {
    axios
      .post(
        "registrosPatronales/selectRegistrosPatronalesItem",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            objetoRegistrosPatronales: (res.data.registrosPatronales || []).map((value) => (
              <Option value={value._id} key={value._id}>
                {value.registroPatronalIMSS}
              </Option>
            )),
          });
        }
      })
      .catch(() => {});

    // El catálogo completo, incluidos los tipos deshabilitados: tienen incidencias
    // históricas reales y el usuario tiene que poder filtrar por ellos.
    axios
      .post(
        "reportesNomina/tiposIncidenciaParaReporte",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            objetoTipos: (res.data.tipos || []).map((t) => (
              <Option value={t._id} key={t._id}>
                {`${t.clave} - ${t.nombre}`}
                {!t.activo && <span style={{ color: "#bbb" }}> (deshabilitado)</span>}
              </Option>
            )),
          });
        }
      })
      .catch(() => {});
  }

  // Cambiar de vista re-consulta: el backend arma una forma distinta según el corte
  // (la rejilla trae las celdas por día, los otros dos no las necesitan).
  //
  // Se llama a `consultar` DIRECTO con los valores del formulario, y no a `form.submit()`:
  // el `Form` del repo trae un candado anti-repetición que ignora cualquier `onFinish`
  // durante los 5 s siguientes al anterior. Con submit(), cambiar de vista justo después de
  // consultar no haría nada y la tabla se quedaría mostrando el corte anterior — sin error,
  // que es lo peor. Los campos ya vienen validados del primer envío.
  cambiarVista(vista) {
    if (vista === this.state.vista) return;
    this.setState({ vista }, () => {
      if (this.state.consultado) {
        this.consultar(this.formRef.current?.getFieldsValue() || {});
      }
    });
  }

  consultar(values) {
    const [fechaInicio, fechaFin] = values.periodo || [];
    this.setState({ loading: true });
    axios
      .post(
        "reportesNomina/reporteIncidencias",
        {
          empresaId: this.props.empresaId,
          fechaInicio: fechaInicio ? fechaInicio.format("YYYY-MM-DD") : null,
          fechaFin: fechaFin ? fechaFin.format("YYYY-MM-DD") : null,
          vista: this.state.vista,
          departamentos: values.departamentos || [],
          empleados: values.empleados || [],
          registrosPatronales: values.registrosPatronales || [],
          puestos: values.puestos || [],
          tiposIncidencia: values.tiposIncidencia || [],
          incluirSinIncidencias: !!values.incluirSinIncidencias,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ loading: false });
        if (res.data.success === 2) {
          this.setState({
            consultado: true,
            empresa: res.data.empresa,
            rango: res.data.rango,
            resumen: res.data.resumen || [],
            columnas: res.data.columnas || [],
            dias: res.data.dias || [],
            periodos: res.data.periodos || [],
            filas: res.data.filas || [],
            trabajadores: res.data.trabajadores || 0,
            descartadosPorRelacion: res.data.descartadosPorRelacion || 0,
            sinClasificar: res.data.sinClasificar || [],
          });
          if ((res.data.filas || []).length === 0) {
            message.info("No se encontraron incidencias con esos filtros.", 6);
          }
        } else if (res.data.success === 1) {
          // Tope de filas o filtro incompleto: el mensaje dice cómo acotar.
          message.warning(res.data.message, 8);
        } else {
          message.error("Hubo un error al generar el reporte");
        }
      })
      .catch(() => {
        this.setState({ loading: false });
        message.error("Hubo un error al generar el reporte");
      });
  }

  // ── Totales de la columna, respetando la unidad ────────────────────────────
  totalDeCubeta(key) {
    return this.state.filas.reduce((s, f) => s + (Number(f.cubetas?.[key]) || 0), 0);
  }

  totalDeTipo(key) {
    return this.state.filas.reduce((s, f) => s + (Number(f.totales?.[key]) || 0), 0);
  }

  // ── Excel ──────────────────────────────────────────────────────────────────
  descargarExcel() {
    const { vista } = this.state;
    const aoa = vista === "dias" ? this.aoaRejilla() : this.aoaTabular();

    const hoja = XLSXStyle.utils.aoa_to_sheet(aoa);
    const ancho = aoa.reduce((m, f) => Math.max(m, f.length), 1);

    hoja["!cols"] = this.anchosExcel(ancho);
    // La banda de título se pinta celda por celda con filaDe(): estilizar sólo A1 deja el
    // resto del merge sin relleno.
    hoja["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(ancho - 1, 1) } }];
    hoja["!rows"] = [{ hpt: 22 }];
    // Nada de !autofilter: se traga la fila de Totales. Nada de !freeze/!panes: no hacen
    // nada fuera de SheetJS Pro.

    const libro = XLSXStyle.utils.book_new();
    const nombreHoja = { general: "Incidencias", nomina: "Por nomina", dias: "Por dia" }[vista];
    XLSXStyle.utils.book_append_sheet(libro, hoja, nombreHoja);
    XLSXStyle.writeFile(
      libro,
      `Incidencias_${vista}_${moment().format("YYYY_MM_DD")}.xlsx`
    );
  }

  // `ancho` es obligatorio: la banda de título se pinta CELDA POR CELDA. Estilizar sólo A1
  // y confiar en el `!merges` deja el resto de la banda sin relleno.
  encabezadoExcel(ancho, filasExtra = []) {
    const { empresa, rango, vista } = this.state;
    const etiqueta = VISTAS.find((v) => v.value === vista)?.label || "";
    return [
      filaDe(ancho, tituloStyle, { 0: empresa?.nombre || "" }),
      [celda(`Incidencias — ${etiqueta}`, subtituloStyle)],
      [celda(`Del ${rango?.desde || ""} al ${rango?.hasta || ""}`, notaStyle)],
      [celda(
        "Las columnas en horas están marcadas (hrs) y no se suman con las de días. "
        + "\"Faltas\" agrupa FINJ + PSS + CAST, igual que el cálculo de nómina.",
        notaStyle
      )],
      ...filasExtra,
      [],
    ];
  }

  // Leyenda de colores para la rejilla. En pantalla el color se explica con el tooltip de
  // la celda; en la hoja no hay tooltip, así que sin esto el color es adivinanza.
  //
  // Va de CUATRO POR RENGLÓN para que caiga en las columnas de identidad, que son las
  // anchas (10/34/22/22). En una columna de día (ancho 8) el texto se cortaría: una celda
  // CON RELLENO no derrama sobre la siguiente como sí lo hace una vacía.
  leyendaColores() {
    const etiquetas = [
      ...this.state.resumen.map((c) => ({ key: c.key, texto: c.titulo })),
      { key: null, texto: "Sin clasificar" },
    ];
    const filas = [[celda("Colores:", notaStyle)]];
    for (let i = 0; i < etiquetas.length; i += 4) {
      filas.push(etiquetas.slice(i, i + 4).map((e) => celda(e.texto, estiloCubeta(e.key))));
    }
    return filas;
  }

  // Columnas de identidad, iguales en los tres cortes salvo las de periodo.
  columnasIdentidad() {
    const base = [
      { titulo: "Código", ancho: 10 },
      { titulo: "Empleado", ancho: 34 },
      { titulo: "Departamento", ancho: 22 },
      { titulo: "Puesto", ancho: 22 },
      { titulo: "Registro Patronal", ancho: 18 },
    ];
    if (this.state.vista === "nomina") {
      base.push(
        { titulo: "Periodo", ancho: 24 },
        { titulo: "Del", ancho: 12 },
        { titulo: "Al", ancho: 12 },
        { titulo: "Estatus", ancho: 18 }
      );
    }
    return base;
  }

  valoresIdentidad(fila) {
    const base = [
      celda(fila.codigo == null ? "" : String(fila.codigo), centroStyle),
      celda(fila.nombre || "", textoStyle),
      celda(fila.departamento || "", textoStyle),
      celda(fila.puesto || "", textoStyle),
      celda(fila.registroPatronalIMSS || "", centroStyle),
    ];
    if (this.state.vista === "nomina") {
      base.push(
        celda(fila.prenominaNombre || "", textoStyle),
        celda(fila.prenominaInicio || "", centroStyle),
        celda(fila.prenominaFin || "", centroStyle),
        celda(fila.estatusLinea || fila.estatusPrenomina || "", centroStyle)
      );
    }
    return base;
  }

  aoaTabular() {
    const { resumen, columnas, filas } = this.state;
    const identidad = this.columnasIdentidad();
    // identidad + cubetas + "Días con incidencia" + una columna por tipo
    const aoa = this.encabezadoExcel(
      identidad.length + resumen.length + 1 + columnas.length,
      this.leyendaColores()
    );

    aoa.push([
      ...identidad.map((c) => celda(c.titulo, headerStyle)),
      ...resumen.map((c) => celda(
        c.unidad === "horas" ? `${c.titulo}\n(hrs)` : c.titulo, headerStyle
      )),
      celda("Días con\nincidencia", headerStyle),
      ...columnas.map((c) => celda(
        `${c.clave}\n${c.unidad === "horas" ? "(hrs)" : "(días)"}`, headerStyle
      )),
    ]);

    for (const fila of filas) {
      aoa.push([
        ...this.valoresIdentidad(fila),
        // Con valor se pinta del color de su cubeta; el cero se queda con el estilo de
        // siempre, que es lo que hace que el color signifique algo.
        ...resumen.map((c) => {
          const v = fila.cubetas?.[c.key] || 0;
          return celdaNumero(v, v > 0 ? estiloCubetaNumero(c.key) : resumenNumeroStyle);
        }),
        celdaNumero(fila.diasConIncidencia || 0, resumenNumeroStyle),
        // En el detalle el color sale de la cubeta a la que pertenece ESA columna, así
        // que FINJ, PSS y CAST salen del mismo rojo que el resumen "Faltas".
        ...columnas.map((c) => {
          const v = fila.totales?.[c.key] || 0;
          return celdaNumero(v, v > 0 ? estiloCubetaNumero(c.cubeta) : centroStyle);
        }),
      ]);
    }

    // Fila de totales. Cada columna se suma sólo consigo misma, así que días y horas
    // nunca se mezclan.
    aoa.push([
      celda("TOTALES", totalTextoStyle),
      ...Array.from({ length: identidad.length - 1 }, () => celda("", totalTextoStyle)),
      ...resumen.map((c) => celdaNumero(this.totalDeCubeta(c.key), totalNumeroStyle)),
      celdaNumero(
        filas.reduce((s, f) => s + (f.diasConIncidencia || 0), 0),
        totalNumeroStyle
      ),
      ...columnas.map((c) => celdaNumero(this.totalDeTipo(c.key), totalNumeroStyle)),
    ]);

    return aoa;
  }

  aoaRejilla() {
    const { dias, filas } = this.state;
    const identidad = this.columnasIdentidad();
    // identidad + un dia por columna + "Días con incidencia"
    const aoa = this.encabezadoExcel(
      identidad.length + dias.length + 1,
      this.leyendaColores()
    );

    aoa.push([
      ...identidad.map((c) => celda(c.titulo, headerStyle)),
      ...dias.map((d) => celda(d.etiqueta.replace(" ", "\n"), headerStyle)),
      celda("Días con\nincidencia", headerStyle),
    ]);

    for (const fila of filas) {
      aoa.push([
        ...this.valoresIdentidad(fila),
        ...dias.map((d) => {
          const entradas = fila.celdas?.[d.iso] || [];
          // El día limpio se queda sin relleno: el contraste contra los pintados es lo
          // que hace legible el calendario.
          if (entradas.length === 0) return celda("", centroStyle);
          // Varias incidencias el mismo día se separan con "/" — pasa con un retardo
          // más una falta parcial, o una incidencia permanente encima de una manual.
          // El color lo da la PRIMERA, igual que en pantalla.
          return celda(
            entradas.map((e) => e.clave).join(" / "),
            estiloCubeta(entradas[0].cubeta)
          );
        }),
        celdaNumero(fila.diasConIncidencia || 0, resumenNumeroStyle),
      ]);
    }

    // En la rejilla el total por día es el CONTEO de empleados con algo ese día: sumar
    // claves de texto no tendría sentido.
    aoa.push([
      celda("EMPLEADOS", totalTextoStyle),
      ...Array.from({ length: identidad.length - 1 }, () => celda("", totalTextoStyle)),
      ...dias.map((d) => celdaNumero(
        filas.filter((f) => (f.celdas?.[d.iso] || []).length > 0).length,
        totalNumeroStyle
      )),
      celdaNumero(filas.reduce((s, f) => s + (f.diasConIncidencia || 0), 0), totalNumeroStyle),
    ]);

    return aoa;
  }

  anchosExcel(ancho) {
    const identidad = this.columnasIdentidad().map((c) => ({ wch: c.ancho }));
    const resto = Array.from({ length: Math.max(ancho - identidad.length, 0) }, () => ({
      wch: this.state.vista === "dias" ? 8 : 12,
    }));
    return [...identidad, ...resto];
  }

  // ── Columnas de la tabla ───────────────────────────────────────────────────
  columnasIdentidadAntd() {
    const { vista } = this.state;
    const cols = [
      {
        title: "Código",
        dataIndex: "codigo",
        key: "codigo",
        width: 80,
        fixed: "left",
        render: (v) => (v == null ? "" : String(v).padStart(4, "0")),
      },
      {
        title: "Empleado",
        dataIndex: "nombre",
        key: "nombre",
        width: 230,
        fixed: "left",
        render: (v, fila) => (
          <span>
            {v}
            {/* Una baja puede tener incidencias legítimas dentro del rango; se marca para
                que no se lea como un empleado que sigue en la plantilla. */}
            {fila.esBaja && <span style={{ color: "#A8071A", fontSize: 11 }}> (baja)</span>}
          </span>
        ),
      },
      { title: "Departamento", dataIndex: "departamento", key: "departamento", width: 150 },
      { title: "Puesto", dataIndex: "puesto", key: "puesto", width: 150 },
    ];
    if (vista === "nomina") {
      cols.push(
        {
          title: "Periodo",
          key: "periodo",
          width: 190,
          render: (_, fila) => (
            <span>
              {fila.prenominaNombre}
              <br />
              <small style={{ color: "#999" }}>
                {fila.prenominaInicio} - {fila.prenominaFin}
              </small>
            </span>
          ),
        },
        {
          title: "Estatus",
          key: "estatus",
          width: 130,
          render: (_, fila) => fila.estatusLinea || fila.estatusPrenomina || "",
        }
      );
    }
    return cols;
  }

  columnasTabulares() {
    const { resumen, columnas } = this.state;
    return [
      ...this.columnasIdentidadAntd(),
      ...resumen.map((c) => ({
        title: (
          <Tooltip title={AYUDA_CUBETA[c.key]}>
            <span style={{ color: colorDeCubeta(c.key), fontWeight: 600 }}>
              {c.titulo}
              {c.unidad === "horas" && <small> (hrs)</small>}{" "}
              <InfoCircleOutlined style={{ color: "#bbb", fontSize: 11 }} />
            </span>
          </Tooltip>
        ),
        key: c.key,
        width: 130,
        align: "center",
        render: (_, fila) => {
          const v = Number(fila.cubetas?.[c.key]) || 0;
          return v === 0 ? CERO : (
            <b style={{ color: colorDeCubeta(c.key) }}>{num(v)}</b>
          );
        },
      })),
      {
        title: "Días con incidencia",
        key: "diasConIncidencia",
        width: 110,
        align: "center",
        render: (_, fila) => (fila.diasConIncidencia ? fila.diasConIncidencia : CERO),
      },
      ...columnas.map((c) => ({
        title: (
          <Tooltip title={`${c.nombre}${c.sinCatalogo ? " (sin tipo en el catálogo)" : ""}`}>
            <span>
              {c.clave}
              <br />
              <small style={{ color: "#999" }}>{c.unidad === "horas" ? "hrs" : "días"}</small>
            </span>
          </Tooltip>
        ),
        key: c.key,
        width: 90,
        align: "center",
        render: (_, fila) => {
          const v = Number(fila.totales?.[c.key]) || 0;
          return v === 0 ? CERO : <span style={{ color: colorDeCubeta(c.cubeta) }}>{num(v)}</span>;
        },
      })),
    ];
  }

  columnasRejilla() {
    const { dias } = this.state;
    return [
      ...this.columnasIdentidadAntd(),
      ...dias.map((d) => ({
        title: (
          <span style={{ color: d.esDomingo ? "#bbb" : undefined, fontSize: 11 }}>
            {d.etiqueta.split(" ")[0]}
            <br />
            {d.dia}
          </span>
        ),
        key: d.iso,
        width: 46,
        align: "center",
        // El domingo va en gris: ayuda a ubicar la semana de un vistazo.
        onHeaderCell: () => ({ style: d.esDomingo ? { backgroundColor: "#fafafa" } : {} }),
        render: (_, fila) => {
          const entradas = fila.celdas?.[d.iso] || [];
          if (entradas.length === 0) return null;
          return (
            <Tooltip
              title={entradas
                .map((e) => `${e.nombre}: ${num(e.cantidad)} ${e.unidad === "horas" ? "hrs" : "día(s)"}`
                  + (e.origen && e.origen !== "manual" ? ` · ${e.origen}` : ""))
                .join(" / ")}
            >
              <span style={{ fontSize: 10, fontWeight: 600, color: colorDeCubeta(entradas[0].cubeta) }}>
                {entradas.map((e) => e.clave).join("/")}
              </span>
            </Tooltip>
          );
        },
      })),
      {
        title: "Días",
        key: "diasConIncidencia",
        width: 60,
        align: "center",
        fixed: "right",
        render: (_, fila) => (fila.diasConIncidencia ? <b>{fila.diasConIncidencia}</b> : CERO),
      },
    ];
  }

  render() {
    const {
      loading, consultado, vista, filas, columnas, dias, resumen,
      trabajadores, descartadosPorRelacion, sinClasificar, periodos,
    } = this.state;

    const esRejilla = vista === "dias";
    const columnasTabla = esRejilla ? this.columnasRejilla() : this.columnasTabulares();
    // El ancho fijo de identidad más lo que crezca el detalle: sin esto la tabla se
    // comprime y las columnas de día quedan ilegibles.
    const anchoIdentidad = vista === "nomina" ? 930 : 610;
    const anchoX = esRejilla
      ? anchoIdentidad + dias.length * 46 + 60
      : anchoIdentidad + resumen.length * 130 + 110 + columnas.length * 90;

    return (
      <>
        <Form
          ref={this.formRef}
          name="reporteIncidencias"
          onFinish={this.consultar.bind(this)}
          onFinishFailed={this.onFinishFailed}
          initialValues={{
            periodo: [moment().startOf("month"), moment().endOf("month")],
            incluirSinIncidencias: false,
          }}
        >
          <PageHeader
            onBack={this.atras.bind(this)}
            title="Incidencias"
            style={{
              border: "1px solid rgb(235, 237, 240)",
              backgroundColor: "white",
              marginBottom: 10,
            }}
            extra={
              <div style={{ display: "flex", justifyContent: "right" }}>
                <Space>
                  {/* El selector de vista NO es un filtro: es el modo de presentación, y
                      por eso vive en el encabezado y re-consulta solo. */}
                  <SegmentedItem
                    options={VISTAS}
                    value={vista}
                    onChange={(v) => this.cambiarVista(v)}
                  />
                  {filas.length > 0 && (
                    <ButtonItem
                      blanco
                      onClick={() => this.descargarExcel()}
                      icon={<FileExcelOutlined />}
                      style={{ display: "block", margin: "0 0 0 auto" }}
                    >
                      Descargar Excel
                    </ButtonItem>
                  )}
                  <ButtonItem
                    type="primary"
                    htmlType="submit"
                    style={{ display: "block", margin: "0 0 0 auto" }}
                  >
                    Consultar
                  </ButtonItem>
                </Space>
              </div>
            }
          />
          <Spin spinning={loading}>
            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingTop: 5,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                marginBottom: 10,
              }}
            >
              {/* forceRender en todas: sin él se pierden los campos de las pestañas que el
                  usuario no llegó a abrir. */}
              <Tabs
                style={{ margin: 0 }}
                tabBarExtraContent={{
                  left: <h1 style={{ padding: 0, margin: "0 10px 0 0" }}>Filtros:</h1>,
                }}
              >
                <TabPane tabKey="1" key="1" forceRender tab="Periodo">
                  <FormItem name="periodo" required noStyle>
                    <RangePickerItem />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="2" key="2" forceRender tab="Departamentos">
                  <FormItem name="departamentos" noStyle margin>
                    <SelectDepartamentosItem
                      mode="multiple"
                      empresaId={this.props.empresaId}
                      headersToken={this.props.headersToken}
                      placeholder="Todos los departamentos"
                      style={{ width: "90%" }}
                    />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="3" key="3" forceRender tab="Empleados">
                  <FormItem name="empleados" noStyle margin>
                    <SelectEmpleadosItem
                      mode="multiple"
                      incluirBajas
                      empresaId={this.props.empresaId}
                      headersToken={this.props.headersToken}
                      placeholder="Todos los empleados"
                      style={{ width: "90%" }}
                    />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="4" key="4" forceRender tab="Tipos de Incidencia">
                  <FormItem name="tiposIncidencia" noStyle margin>
                    <SelectItem
                      width="90%"
                      mode="multiple"
                      placeholder="Todos los tipos de incidencia"
                    >
                      {this.state.objetoTipos}
                    </SelectItem>
                  </FormItem>
                </TabPane>
                <TabPane tabKey="5" key="5" forceRender tab="Registro Patronal">
                  <FormItem name="registrosPatronales" noStyle margin>
                    <SelectItem
                      width="90%"
                      mode="multiple"
                      placeholder="Todos los registros patronales"
                    >
                      {this.state.objetoRegistrosPatronales}
                    </SelectItem>
                  </FormItem>
                </TabPane>
                <TabPane tabKey="6" key="6" forceRender tab="Puestos">
                  <FormItem name="puestos" noStyle margin>
                    <SelectPuestosItem
                      mode="multiple"
                      empresaId={this.props.empresaId}
                      headersToken={this.props.headersToken}
                      placeholder="Todos los puestos"
                      style={{ width: "90%" }}
                    />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="7" key="7" forceRender tab="Opciones">
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }}>
                    {/* Por omisión sólo salen los que tuvieron algo, que es la pregunta
                        normal. Encendido sirve para el corte por departamento, donde también
                        importa quién salió limpio. No aplica a "Por nómina": un renglón por
                        recibo no existe si no hubo recibo. */}
                    <FormItem
                      name="incluirSinIncidencias"
                      label="Incluir empleados sin incidencias"
                      valuePropName="checked"
                      margin
                    >
                      <SwitchItem disabled={vista === "nomina"} />
                    </FormItem>
                    {vista === "nomina" && (
                      <span style={{ color: "#999" }}>
                        No aplica en la vista por nómina.
                      </span>
                    )}
                  </div>
                </TabPane>
              </Tabs>
            </div>

            {consultado && filas.length > 0 && (
              <div
                style={{
                  backgroundColor: "white",
                  padding: 20,
                  paddingTop: 10,
                  marginBottom: 15,
                  borderRadius: 5,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                }}
              >
                <p style={{ marginBottom: 4, color: "#666" }}>
                  {trabajadores} empleado(s) · {filas.length} renglón(es)
                  {periodos.length > 0 && <> · {periodos.length} periodo(s)</>}
                  {" · "}
                  {resumen.map((c, i) => (
                    <span key={c.key}>
                      {i > 0 && " · "}
                      {c.titulo}{" "}
                      <b style={{ color: colorDeCubeta(c.key) }}>
                        {num(this.totalDeCubeta(c.key))}
                      </b>
                      {c.unidad === "horas" && " hrs"}
                    </span>
                  ))}
                </p>

                {/* Lo que se descartó se DICE. Un descarte silencioso es lo que haría que
                    el usuario confiara en un número incompleto. */}
                {descartadosPorRelacion > 0 && (
                  <p style={{ marginBottom: 4, color: "#FA8C16" }}>
                    Se descartaron {descartadosPorRelacion} incidencia(s) capturadas en días
                    fuera de la relación laboral del empleado (antes del alta o después de la
                    baja). Suelen venir del reloj checador, que marca ausencia en todo día con
                    horario aunque el empleado ya no esté.
                  </p>
                )}
                {sinClasificar.length > 0 && (
                  <p style={{ marginBottom: 4, color: "#FA8C16" }}>
                    Hay incidencias con un tipo que ya no existe en el catálogo:{" "}
                    <b>{sinClasificar.join(", ")}</b>. Salen en su propia columna pero no
                    suman a ninguna cubeta del resumen.
                  </p>
                )}

                <Table
                  size="small"
                  rowKey="clave"
                  dataSource={filas}
                  columns={columnasTabla}
                  pagination={{ defaultPageSize: esRejilla ? 25 : 20 }}
                  scroll={{ x: anchoX }}
                  bordered={esRejilla}
                />
              </div>
            )}
          </Spin>
        </Form>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(ReporteIncidencias);
